import { useEffect, useState } from 'react';
import type { WidgetProps } from '@frontend/widgets/types';
import { fetchWidgetData } from '../api';

interface Category {
	id: string;
	label: string;
	total: number;
	open: number;
	closed: number;
	completionPercent: number;
	recent: Array<{
		id: number;
		subject: string;
		status: string;
		closed: boolean;
		href: string | null;
	}>;
}

export default function OpenProjectDeliveryBreakdownWidget({
	contextType,
	contextId,
	ecosystemId,
}: WidgetProps) {
	const [categories, setCategories] = useState<Category[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	useEffect(() => {
		if (contextType !== 'project') {
			setError('Delivery breakdown is available on project views only.');
			setLoading(false);
			return;
		}
		setLoading(true);
		setError('');
		fetchWidgetData<{ categories: Category[] }>('openproject-delivery-breakdown', {
			ecosystemId,
			contextType,
			contextId,
		})
			.then((d) => setCategories(d.categories || []))
			.catch((err: Error) => setError(err.message))
			.finally(() => setLoading(false));
	}, [contextType, contextId, ecosystemId]);

	if (loading) return <div className="widget-inner-loading">Loading delivery analytics…</div>;
	if (error) return <div className="widget-inner-error">{error}</div>;
	if (!categories.some((c) => c.total > 0)) {
		return <div className="widget-inner-empty">No epics, stories, or tasks found for this project.</div>;
	}

	return (
		<div className="widget-delivery-breakdown">
			<div className="tree-dashboard-metrics">
				{categories.map((category) => (
					<div key={category.id} className="tree-dashboard-metric-card">
						<div className="tree-dashboard-metric-label">{category.label}</div>
						<div className="tree-dashboard-metric-value">
							{category.closed}/{category.total}
						</div>
						<div className="widget-progress-bar" aria-hidden="true">
							<span style={{ width: `${category.completionPercent}%` }} />
						</div>
						<div className="widget-list-meta">
							<span>{category.open} open</span>
							<span>{category.completionPercent}% done</span>
						</div>
					</div>
				))}
			</div>
			{categories.map((category) =>
				category.recent.length ? (
					<div key={`${category.id}-recent`} className="widget-category-recent">
						<h4 className="widget-subtitle">Recent {category.label}</h4>
						<ul className="widget-list">
							{category.recent.map((item) => (
								<li key={item.id} className="widget-list-item">
									<div className="widget-list-primary">
										{item.href ? (
											<a href={item.href} target="_blank" rel="noopener noreferrer">
												{item.subject}
											</a>
										) : (
											<span>{item.subject}</span>
										)}
									</div>
									<div className="widget-list-meta">
										<span className="widget-status-badge">{item.status}</span>
									</div>
								</li>
							))}
						</ul>
					</div>
				) : null
			)}
		</div>
	);
}
